import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/services/auth'
import { shouldShowDevTools } from '@/utils/environment'

interface DevelopmentRouteProps {
  children: ReactNode
}

/**
 * Route guard for development tools and test pages
 *
 * Access Policy: Centralized in shouldShowDevTools() function
 * - See utils/environment.ts for current policy configuration
 * - Policy can be changed via VITE_DEV_TOOLS_ACCESS env var
 *
 * Shows loading state while checking authentication if policy requires it.
 *
 * @example
 * <Route path="/dev/test" element={
 *   <DevelopmentRoute>
 *     <TestPage />
 *   </DevelopmentRoute>
 * } />
 */
export function DevelopmentRoute({ children }: DevelopmentRouteProps) {
  const { isAdmin, isLoading } = useAuth()

  // Check centralized policy
  const canAccessDevTools = shouldShowDevTools(isAdmin)

  // If policy allows access, render immediately
  if (canAccessDevTools) {
    return <>{children}</>
  }

  // If policy requires auth and we're still loading, show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Policy denies access - redirect to home
  return <Navigate to="/" replace />
}
